import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CreditCard, Upload, Save, QrCode, Smartphone, RefreshCw, Zap } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate UPI QR code URL using free API
const generateUPIQRCode = (upiId, merchantName, amount) => {
  const upiString = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(merchantName)}&am=${amount}&cu=INR`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(upiString)}`;
};

const PaymentSettingsManagement = () => {
  const [settings, setSettings] = useState({
    upiId: 'dropsquadz@okicici',
    merchantName: 'DropSquadz',
    whatsappNumber: '919285110409',
    plans: {
      Basic: { price: 999, qrCode: '', customQR: false },
      Advanced: { price: 1999, qrCode: '', customQR: false },
      Professional: { price: 2999, qrCode: '', customQR: false }
    },
    upgradeDiscount: 1000
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPlan, setUploadingPlan] = useState(null);
  
  const fileInputRefs = {
    Basic: useRef(null),
    Advanced: useRef(null),
    Professional: useRef(null)
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Auto-generate QR codes when UPI ID or prices change
  useEffect(() => {
    if (settings.upiId && settings.merchantName) {
      setSettings(prev => ({
        ...prev,
        plans: {
          Basic: {
            ...prev.plans.Basic,
            qrCode: prev.plans.Basic.customQR ? prev.plans.Basic.qrCode : generateUPIQRCode(prev.upiId, prev.merchantName, prev.plans.Basic.price)
          },
          Advanced: {
            ...prev.plans.Advanced,
            qrCode: prev.plans.Advanced.customQR ? prev.plans.Advanced.qrCode : generateUPIQRCode(prev.upiId, prev.merchantName, prev.plans.Advanced.price)
          },
          Professional: {
            ...prev.plans.Professional,
            qrCode: prev.plans.Professional.customQR ? prev.plans.Professional.qrCode : generateUPIQRCode(prev.upiId, prev.merchantName, prev.plans.Professional.price)
          }
        }
      }));
    }
  }, [settings.upiId, settings.merchantName, settings.plans.Basic?.price, settings.plans.Advanced?.price, settings.plans.Professional?.price]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings`);
      const data = response.data;
      
      // Ensure all plans have the customQR flag
      if (data.plans) {
        Object.keys(data.plans).forEach(plan => {
          if (data.plans[plan].customQR === undefined) {
            data.plans[plan].customQR = false;
          }
        });
      }
      
      setSettings(data);
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/payment-settings`, settings);
      toast({
        title: '✅ Success',
        description: 'Payment settings saved! QR codes auto-generated.',
      });
      // Update with response data
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.response?.data?.detail || 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleQRUpload = async (plan, file) => {
    if (!file) return;
    
    setUploadingPlan(plan);
    try {
      const formData = new FormData();
      formData.append('plan', plan);
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/payment-settings/upload-qr`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update local state with custom QR URL
      setSettings(prev => ({
        ...prev,
        plans: {
          ...prev.plans,
          [plan]: {
            ...prev.plans[plan],
            qrCode: response.data.qrUrl,
            customQR: true
          }
        }
      }));
      
      toast({
        title: '✅ QR Uploaded',
        description: `Custom QR for ${plan} plan uploaded`,
      });
    } catch (error) {
      toast({
        title: '❌ Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload QR code',
        variant: 'destructive',
      });
    } finally {
      setUploadingPlan(null);
    }
  };

  const handlePriceChange = (plan, price) => {
    const newPrice = parseFloat(price) || 0;
    setSettings(prev => ({
      ...prev,
      plans: {
        ...prev.plans,
        [plan]: {
          ...prev.plans[plan],
          price: newPrice,
          // Auto-generate new QR if not custom
          qrCode: prev.plans[plan].customQR 
            ? prev.plans[plan].qrCode 
            : generateUPIQRCode(prev.upiId, prev.merchantName, newPrice)
        }
      }
    }));
  };

  const handleUseAutoQR = (plan) => {
    // Reset to auto-generated QR
    setSettings(prev => ({
      ...prev,
      plans: {
        ...prev.plans,
        [plan]: {
          ...prev.plans[plan],
          qrCode: generateUPIQRCode(prev.upiId, prev.merchantName, prev.plans[plan].price),
          customQR: false
        }
      }
    }));
    toast({
      title: '🔄 Reset to Auto QR',
      description: `${plan} plan now uses auto-generated QR`,
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600">Loading payment settings...</p>
      </div>
    );
  }

  const planColors = {
    Basic: 'from-blue-500 to-blue-500',
    Advanced: 'from-blue-500 to-blue-500',
    Professional: 'from-orange-500 to-red-500'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-blue-600 flex items-center">
          <CreditCard className="w-6 h-6 mr-2 text-green-600" />
          Payment Settings
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={fetchSettings}
            variant="outline"
            className="border-slate-200 hover:bg-slate-100"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg hover:shadow-green-500/50 transition-all hover:scale-105"
          >
            {saving ? (
              <div className="flex items-center">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Saving...
              </div>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Auto QR Info */}
      <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <Zap className="w-6 h-6 text-green-600 mt-1" />
            <div>
              <h4 className="font-semibold text-green-600 mb-1">🚀 Auto QR Generation</h4>
              <p className="text-sm text-slate-600">
                QR codes are automatically generated when you change UPI ID or Price. 
                Just update the values and click Save!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* UPI Settings */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg text-blue-400 flex items-center">
            <Smartphone className="w-5 h-5 mr-2" />
            UPI Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600 font-medium">UPI ID *</Label>
              <Input
                value={settings.upiId}
                onChange={(e) => setSettings({ ...settings, upiId: e.target.value })}
                placeholder="yourname@upi"
                className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
              />
              <p className="text-xs text-slate-400 mt-1">QR codes will auto-generate based on this</p>
            </div>
            <div>
              <Label className="text-slate-600 font-medium">Merchant Name *</Label>
              <Input
                value={settings.merchantName}
                onChange={(e) => setSettings({ ...settings, merchantName: e.target.value })}
                placeholder="Your Business Name"
                className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
              />
              <p className="text-xs text-slate-400 mt-1">Shown in UPI payment apps</p>
            </div>
          </div>
          <div>
            <Label className="text-slate-600 font-medium">Support WhatsApp Number *</Label>
            <Input
              value={settings.whatsappNumber || ''}
              onChange={(e) => setSettings({ ...settings, whatsappNumber: e.target.value.replace(/[^0-9]/g, '') })}
              placeholder="919285110409 (with country code, no +)"
              inputMode="numeric"
              className="bg-slate-50 border-slate-200 text-slate-800 mt-2 w-full md:w-96"
              data-testid="admin-whatsapp-number-input"
            />
            <p className="text-xs text-slate-400 mt-1">
              Used for the "Connect on WhatsApp" step in client dashboard. Include country code without "+" (e.g. 91 for India).
            </p>
          </div>
          <div>
            <Label className="text-slate-600 font-medium">Upgrade Discount (₹)</Label>
            <Input
              type="number"
              value={settings.upgradeDiscount}
              onChange={(e) => setSettings({ ...settings, upgradeDiscount: parseFloat(e.target.value) || 0 })}
              placeholder="1000"
              className="bg-slate-50 border-slate-200 text-slate-800 mt-2 w-48"
            />
            <p className="text-xs text-slate-400 mt-1">Discount applied when user upgrades plan</p>
          </div>
        </CardContent>
      </Card>

      {/* Plan QR Codes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {['Basic', 'Advanced', 'Professional'].map((plan) => (
          <Card key={plan} className={`bg-gradient-to-br ${planColors[plan]}/10 border-slate-200 hover:border-white/30 transition-all`}>
            <CardHeader>
              <CardTitle className={`text-lg bg-gradient-to-r ${planColors[plan]} bg-clip-text text-transparent flex items-center justify-between`}>
                <span className="flex items-center">
                  <QrCode className="w-5 h-5 mr-2" />
                  {plan} Plan
                </span>
                {settings.plans[plan]?.customQR && (
                  <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">Custom</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Price */}
              <div>
                <Label className="text-slate-600 font-medium">Price (₹)</Label>
                <Input
                  type="number"
                  value={settings.plans[plan]?.price || ''}
                  onChange={(e) => handlePriceChange(plan, e.target.value)}
                  placeholder="999"
                  className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                />
              </div>

              {/* QR Code Preview */}
              <div>
                <Label className="text-slate-600 font-medium flex items-center justify-between">
                  <span>QR Code</span>
                  {!settings.plans[plan]?.customQR && (
                    <span className="text-xs text-green-600">Auto-Generated ✓</span>
                  )}
                </Label>
                <div className="mt-2 border border-slate-200 rounded-lg p-2 bg-white">
                  {settings.plans[plan]?.qrCode ? (
                    <img
                      src={settings.plans[plan].qrCode}
                      alt={`${plan} QR`}
                      className="w-full h-auto rounded"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/200x200?text=QR+Error';
                      }}
                    />
                  ) : (
                    <div className="w-full h-48 flex items-center justify-center text-slate-400 bg-slate-100 rounded">
                      <QrCode className="w-12 h-12" />
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="space-y-2">
                <input
                  ref={fileInputRefs[plan]}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleQRUpload(plan, e.target.files[0])}
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRefs[plan].current?.click()}
                  disabled={uploadingPlan === plan}
                  variant="outline"
                  className="w-full border-slate-200 hover:bg-slate-100"
                  size="sm"
                >
                  {uploadingPlan === plan ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Uploading...
                    </div>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Custom QR
                    </>
                  )}
                </Button>
                {settings.plans[plan]?.customQR && (
                  <Button
                    onClick={() => handleUseAutoQR(plan)}
                    variant="ghost"
                    className="w-full hover:bg-green-500/10 text-green-600"
                    size="sm"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Use Auto QR
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Instructions */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="pt-6">
          <h4 className="font-semibold text-blue-400 mb-3">📋 How It Works:</h4>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>1. Enter your <strong>UPI ID</strong> (e.g., yourname@okicici, shop@paytm)</li>
            <li>2. Set the <strong>Price</strong> for each plan</li>
            <li>3. QR codes are <strong>automatically generated</strong> with amount filled!</li>
            <li>4. Click <strong>&quot;Save Settings&quot;</strong> to apply changes</li>
            <li>5. (Optional) Upload custom QR images if needed</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSettingsManagement;
